// A totals row whose figures sit UNDER their columns (BP-91, H179) — one
// figure or several. Grid-agnostic: it is handed the visible columns' widths
// and left offsets and the body's horizontal scroll, and lays the row out from
// them. The two wrappers read those from their own grid, because the Premium
// detail-tab grid (v7) and the Pro list grid (v6) do not share a React context:
//   · ColumnAlignedTotal — one figure, a detail tab's footer (Premium);
//   · ListColumnTotals   — any number, a list's footer (Pro; AeronetDataGrid
//     `columnTotals`, PAT-10's totals row on the Flights list).
//
// The label fills every column left of the FIRST figure; then each column up
// to the LAST figure is its own width, holding its figure or nothing. No pixel
// offsets are written here; every width is the grid's. When none of the
// figures' columns is visible the row falls back to BP-80's right-hand block
// (label, then each figure) rather than guessing a place.
import type { ReactNode } from "react";
import { Box } from "@mui/material";

import { detailTotalsSummaryCellSx, detailTotalsSummaryRowSx } from "../../utils/dataGridSx";

export interface AlignedColumn {
  field: string;
  computedWidth: number;
}

export interface AlignedTotalsRowProps {
  /** The grid's VISIBLE columns, in order, and each one's left offset. */
  columns: readonly AlignedColumn[];
  positions: readonly number[];
  /** The body's horizontal scroll, so the row follows it. */
  scrollLeft: number;
  label: ReactNode;
  /** field → its figure. A field the grid is not showing has no place. */
  values: Readonly<Record<string, ReactNode>>;
  labelTestId?: string;
  /** The figure's test id, per field. */
  valueTestId?: (field: string) => string | undefined;
  fallbackTestId?: string;
}

/** The grid's cell padding (MUI X `.MuiDataGrid-cell`), so a figure's digits
 *  land on the column's own right edge, like its cells. */
const CELL_PADDING_X = "10px";

export function AlignedTotalsRow({
  columns,
  positions,
  scrollLeft,
  label,
  values,
  labelTestId,
  valueTestId,
  fallbackTestId = "column-aligned-total-fallback",
}: AlignedTotalsRowProps) {
  const rowSx = { ...detailTotalsSummaryRowSx, borderBottom: "none" };
  const fields = Object.keys(values);
  const placed = columns
    .map((c, i) => (fields.includes(c.field) ? i : -1))
    .filter((i) => i >= 0);

  if (placed.length === 0) {
    return (
      <Box
        sx={{ ...rowSx, gridTemplateColumns: `auto repeat(${Math.max(fields.length, 1)}, minmax(100px, max-content))` }}
        data-testid={fallbackTestId}
      >
        <Box sx={detailTotalsSummaryCellSx} data-testid={labelTestId}>
          {label}
        </Box>
        {fields.map((f) => (
          <Box key={f} sx={detailTotalsSummaryCellSx} data-testid={valueTestId?.(f)} data-field={f}>
            {values[f]}
          </Box>
        ))}
      </Box>
    );
  }

  const first = placed[0];
  const last = placed[placed.length - 1];
  const left = positions[first] ?? 0;
  const cellSx = { ...detailTotalsSummaryCellSx, flex: "0 0 auto", px: CELL_PADDING_X, boxSizing: "border-box" };
  return (
    <Box sx={{ ...rowSx, display: "block", px: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", transform: `translateX(${-scrollLeft}px)` }}>
        {/* Everything left of the first figure: the label, flush against it. */}
        <Box
          sx={{ ...cellSx, width: left, overflow: "hidden", textOverflow: "ellipsis" }}
          data-testid={labelTestId}
          data-aligned-width={left}
        >
          {label}
        </Box>
        {columns.slice(first, last + 1).map((c, k) => {
          const has = fields.includes(c.field);
          return (
            <Box
              key={c.field}
              sx={{ ...cellSx, width: c.computedWidth }}
              data-testid={valueTestId?.(c.field)}
              data-field={c.field}
              data-aligned-left={positions[first + k] ?? 0}
              data-aligned-width={c.computedWidth}
            >
              {has ? values[c.field] : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default AlignedTotalsRow;
